import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import AdminLoginModal from './AdminLoginModal';

const FooterContainer = styled.footer`
  background: white;
  color: #666;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  border-top: 1px solid #e0e0e0;
  position: relative;
`;

const FooterContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr 0.8fr;
  gap: ${props => props.theme.spacing.xxl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const Logo = styled.img`
  height: 50px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  margin-bottom: ${props => props.theme.spacing.sm};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    height: 40px;
  }
`;

const CompanyDescription = styled.p`
  color: #666;
  line-height: 1.5;
  font-size: 0.85rem;
  margin-top: ${props => props.theme.spacing.sm};
`;

const NavSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.lg};
  border-left: 2px solid #e0e0e0;
  padding-left: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.md};
    border-left: none;
    border-top: 2px solid #e0e0e0;
    padding-left: ${props => props.theme.spacing.lg};
    padding-top: ${props => props.theme.spacing.xl};
    margin-top: ${props => props.theme.spacing.lg};
  }
`;

const NavLink = styled(Link)`
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  transition: color ${props => props.theme.transitions.fast};

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const CompanyInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const InfoText = styled.p`
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
  margin: 0;

  strong {
    color: #333;
    font-weight: 600;
  }
`;

const ContactGroup = styled.div`
  margin-top: ${props => props.theme.spacing.md};
`;

const ContactTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 700;
  color: #333;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ContactInfo = styled.div`
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;

  span {
    display: inline-block;
    margin-right: ${props => props.theme.spacing.lg};

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      display: block;
      margin-right: 0;
    }
  }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.xl};
  border-top: 1px solid #e0e0e0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
    align-items: flex-start;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const FooterLink = styled.button`
  background: transparent;
  border: none;
  color: #666;
  font-size: 0.85rem;
  cursor: pointer;
  transition: color ${props => props.theme.transitions.fast};
  padding: 0;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const LinkDivider = styled.span`
  color: #ccc;
  font-size: 0.85rem;
`;

const Copyright = styled.div`
  font-size: 0.85rem;
  color: #999;
`;

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  return (
    <>
      <FooterContainer>
        <FooterContent>
          <FooterTop>
            {/* 로고 섹션 */}
            <LogoSection>
              <Logo src="/img/naw_mainlogo_full.png" alt="(주)나우바이오" />
              <CompanyDescription>
                {t('about.description')}
              </CompanyDescription>
            </LogoSection>

            {/* 회사 정보 */}
            <CompanyInfoSection>
              <ContactGroup>
                <ContactTitle>{t('about.location.headquarters.title')}</ContactTitle>
                <InfoText>
                  <strong>{t('about.location.address')}:</strong> {t('about.location.headquarters.address')}
                </InfoText>
                <ContactInfo>
                  <span><strong>Tel:</strong> {t('about.location.headquarters.tel')}</span>
                  <span><strong>Fax:</strong> {t('about.location.headquarters.fax')}</span>
                  <span><strong>Email:</strong> {t('about.company.emailValue')}</span>
                </ContactInfo>
              </ContactGroup>

              <ContactGroup>
                <ContactTitle>{t('about.location.office.title')}</ContactTitle>
                <InfoText>
                  <strong>{t('about.location.address')}:</strong> {t('about.location.office.address')}
                </InfoText>
                <ContactInfo>
                  <span><strong>Tel:</strong> {t('about.location.office.tel')}</span>
                  <span><strong>Fax:</strong> {t('about.location.office.fax')}</span>
                </ContactInfo>
              </ContactGroup>
            </CompanyInfoSection>

            {/* 네비게이션 섹션 */}
            <NavSection>
              <NavLink to="/about?tab=overview">{t('nav.aboutUs')}</NavLink>
              <NavLink to="/products">{t('nav.product')}</NavLink>
              <NavLink to="/inquiry">{t('nav.support')}</NavLink>
              <NavLink to="/news">{t('nav.news')}</NavLink>
            </NavSection>
          </FooterTop>

          <FooterBottom>
            <Copyright>
              {t('footer.copyright')}
            </Copyright>
            <FooterLinks>
              <FooterLink onClick={() => setShowPrivacyPolicy(true)}>
                {t('footer.privacyPolicy')}
              </FooterLink>
              <LinkDivider>|</LinkDivider>
              <FooterLink onClick={() => setShowAdminLogin(true)}>
                관리자로그인
              </FooterLink>
            </FooterLinks>
          </FooterBottom>
        </FooterContent>
      </FooterContainer>

      <PrivacyPolicyModal
        isOpen={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />

      <AdminLoginModal
        isOpen={showAdminLogin}
        onClose={() => setShowAdminLogin(false)}
      />
    </>
  );
};

export default Footer;
